/**
 * TypeScript interfaces for AI Search Preview Component
 * Generated from data model specifications
 */

export interface AIPreviewRequest {
  prompt: string;
  session_id: string;
  timestamp: Date;
}

/**
 * A source grounding the AI answer, parsed from the backend's `citations:`
 * SSE event (emitted when the agent's knowledge tool returns results).
 */
export interface AICitation {
  url: string;
  title: string;
  type?: string;
  snippet?: string;
}

export interface AIResponseMetadata {
  responseTime: number;
  tokenCount?: number;
  confidence?: number;
  sources?: AICitation[];
  generatedAt: Date;
}

/**
 * Hand-off from the compact AI Overview into the conversational AI Mode view:
 * the first turn (question + already-streamed answer + sources) seeds the
 * thread so no request is repeated, and the shared session_id lets the
 * backend continue the same conversation.
 */
export interface AIModeSeed {
  question: string;
  answer: string;
  sources?: AICitation[];
  // The session id the overview's stream ACTUALLY used — follow-ups must
  // reuse it or the backend conversation context is lost.
  sessionId?: string;
}

export interface AIPreviewResponse {
  content: string;
  streamedContent: string;
  contentType: 'data' | 'tool_use' | 'mcp_ui';
  isComplete: boolean;
  error?: string;
  metadata?: AIResponseMetadata;
}

export interface PreviewDisplayState {
  isVisible: boolean;
  isExpanded: boolean;
  previewLines: string[];
  totalLines: string[];
  isStreaming: boolean;
  streamingCursor: boolean;
  animationState: 'idle' | 'expanding' | 'collapsing';
}

export interface SearchIntegrationState {
  searchQuery: string;
  shouldTriggerAI: boolean;
  lastSearchQuery: string;
  expansionPreference: 'remember' | 'reset';
  isEnabled: boolean;
}

export interface AIPreviewError {
  type:
    | 'network'
    | 'timeout'
    | 'parsing'
    | 'validation'
    | 'quota'
    | 'component';
  message: string;
  details?: string;
  isRetryable: boolean;
  retryAfter?: number;
  timestamp?: Date;
  errorId?: string;
}

export interface AccessibilityState {
  ariaLabel: string;
  ariaLiveRegion: string;
  focusManagement: 'auto' | 'manual';
  reducedMotion: boolean;
  hasKeyboardFocus: boolean;
}

export enum AIPreviewState {
  IDLE = 'idle',
  LOADING = 'loading',
  STREAMING = 'streaming',
  COMPLETE = 'complete',
  ERROR = 'error',
  EMPTY = 'empty',
}

export interface AIStreamingConfig {
  apiEndpoint: string;
  apiKey: string;
  headers: Record<string, string>;
  timeout: number;
  maxRetries: number;
}
